import { FormControlLabel } from "@mui/material";
import { styled } from "@mui/material/styles";
import CalendarCheckFillIcon from "remixicon-react/CalendarCheckFillIcon";
import dateformat from "dateformat";
import Switch from "@mui/material/Switch";
import { useState } from "react";
import { createUseStyles } from "$utils/createUseStyles";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "$utils/useSnackbar";
import {
  getCompanyTypeLabel,
  getCompanyTypeColor,
} from "$utils/typeConstants/Company";
import { DEFAULT_DATE_FORMAT } from "$utils/utils";
import { theme } from "$utils/theme/bataillog";
import { CompanyServices } from "$services";
import { CacheInvalidationAction } from "$utils/cache";
import { snackbarErrorOptions } from "$utils/SnackbarOptions";

const COMPANY_DEACTIVATION_BLOCKED_MESSAGE =
  "Cette entreprise ne peut pas être désactivée car des EDB ou préparations sont en cours.";

const isCompanyDeactivationBlockedError = (message?: string | null) =>
  !!message?.includes("Impossible de désactiver cette company");

type Props = {
  company: any;
};

const HeaderRow = styled("div")({
  display: "flex",
  marginLeft: 18,
  alignItems: "center",
});

const CalendarBlock = styled("div")({
  display: "flex",
  padding: "0 18px",
  alignItems: "center",
  borderLeft: `1px solid ${theme.color.gray2}`,
  borderRight: `1px solid ${theme.color.gray2}`,
  color: theme.color.gray5,
  fontSize: 14,
  marginRight: 18,
});

const CalendarIcon = styled(CalendarCheckFillIcon)({
  width: 18,
  marginRight: theme.spacing(1),
});

export const CompanyHeader = ({ company }: Props) => {
  const { companyTypes } = company || {};
  const badgeClasses = useStyles();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openSnackbarError] = useSnackbar(snackbarErrorOptions);
  const queryClient = useQueryClient();

  const { data: parentCompany } = useQuery({
    queryKey: [
      CacheInvalidationAction.GET_COMPANY_BY_ID,
      company?.agencyOf?._id,
    ],
    queryFn: () => CompanyServices.getCompanyById(company?.agencyOf?._id),
    enabled: !!company?.agencyOf?._id,
    gcTime: 60 * 1000,
  });

  const toggleActiveCompany = async () => {
    setIsLoading(true);
    try {
      const result = await CompanyServices.editCompany(company._id, {
        ...company,
        isActive: !company?.isActive,
      });

      if (result?.error) {
        openSnackbarError(
          isCompanyDeactivationBlockedError(result.error)
            ? COMPANY_DEACTIVATION_BLOCKED_MESSAGE
            : result.error
        );
        return;
      }

      await queryClient.invalidateQueries({
        queryKey: [CacheInvalidationAction.GET_COMPANY_BY_ID, company._id],
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <HeaderRow>
      {(companyTypes || []).map((ct: any) => (
        <span
          key={ct}
          className={badgeClasses.companyTypeBadge}
          style={{ background: getCompanyTypeColor(ct, theme) }}
        >
          {getCompanyTypeLabel(ct)}
        </span>
      ))}

      <CalendarBlock>
        <CalendarIcon />
        Créée le{" "}
        {company?.createdAt &&
          dateformat(new Date(company?.createdAt), DEFAULT_DATE_FORMAT)}
      </CalendarBlock>
      <FormControlLabel
        sx={{ "& span": { fontSize: 14 } }}
        control={
          <Switch
            disabled={(parentCompany && !parentCompany?.isActive) || isLoading}
            checked={company?.isActive}
            onChange={toggleActiveCompany}
            name="activeCompany"
            color="primary"
          />
        }
        label="Entreprise active"
      />
    </HeaderRow>
  );
};

export default CompanyHeader;

const useStyles = createUseStyles(() => ({
  companyTypeBadge: {
    marginRight: 16,
    color: "white",
    padding: "4px 8px",
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 600,
  },
}));
